import React, { FC } from 'react';

import { MarketingCardGroup } from 'features/content/components/marketing-card-group/marketing-card-group';

import { useContentCardsNotificationsExperiment } from 'experiments/use-content-cards-notifications';
import { IMarketingCard } from 'generated/sanity-graphql';
import {
  MAX_CONTENT_CARDS_DISPLAYED,
  filterContentCardsByServiceMode,
} from 'state/braze/hooks/utils';
import { ContentCardTypes } from 'state/braze/types';
import { selectors, useAppSelector } from 'state/global-state';
import { useServiceModeLive } from 'state/service-mode';

import { IMarketingCardGroupProps } from '../../content/components/marketing-card-group/types';
import {
  hasRules,
  tileShouldBeDisplayed,
} from '../../content/components/marketing-card-group/utils';
import { BrazeMarketingCard } from '../../notifications/components/braze-marketing-card';

/**
 *
 * Selects Home feed content while shared content owns the card grid.
 *
 */
export const HomeMarketingCardGroup: FC<React.PropsWithChildren<IMarketingCardGroupProps>> = ({
  doc,
  onShowTermsModal,
}) => {
  const { isHomePageFeedEnabled, isNewsFeedEnabled } = useContentCardsNotificationsExperiment();
  const brazeContentCards = useAppSelector(selectors.loyalty.selectHomePageFeedContentCards);
  const serviceMode = useServiceModeLive();

  const cards = doc.Cards as IMarketingCard[];

  if (!cards || cards.length <= 0) {
    return null;
  }

  const brazeContentCardsList = isHomePageFeedEnabled ? brazeContentCards : [];
  const visibleBrazeContentCardsList = filterContentCardsByServiceMode(
    brazeContentCardsList,
    serviceMode
  );
  const topVisibleBrazeContentCardsList = visibleBrazeContentCardsList
    .filter(card => card.type === ContentCardTypes.CAPTIONED)
    .slice(0, MAX_CONTENT_CARDS_DISPLAYED);

  // NOTE: Return all cards, except those cards that have rule and do not meet the requirement
  const sanityCardsList = cards.filter(card => {
    if (hasRules(card) && !tileShouldBeDisplayed(card)) {
      return false;
    }
    return true;
  });

  // Sanity cards only when Braze homepage feed is off (no fallback when Braze is on but empty)
  const cardsList = !isNewsFeedEnabled && !isHomePageFeedEnabled ? sanityCardsList : [];

  if (topVisibleBrazeContentCardsList.length === 0 && cardsList.length === 0) {
    return null;
  }

  return (
    <MarketingCardGroup cards={cardsList} onShowTermsModal={onShowTermsModal}>
      {topVisibleBrazeContentCardsList.map((card, index) => {
        const key = `BrazeMarketingCard_${card.id}`;
        return (
          <BrazeMarketingCard
            key={key}
            card={card}
            onShowTermsModal={onShowTermsModal}
            index={index}
          />
        );
      })}
    </MarketingCardGroup>
  );
};
